import { UseQueryOptions, UseMutationOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { ApiCalls } from '../api/api';
import { InitialData, TimeUserFeatures } from '../api/generated/data-contracts';
import { useSetQueryData } from './useSetQueryData';
import { configQKey, useConfigValues } from './queries/useConfigValues';
import { INIT_DATA_V2 } from '../constants/queryKeys';
import { FunctionOr } from '../types/FunctionOr';
import { unwrap } from '../helpers/unwrap';

const useSetConfigValuesCache = () => {
    return useSetQueryData<InitialData>(configQKey);
};

const useInvalidateV2InitData = () => {
    const queryClient = useQueryClient();
    return useCallback(() => {
        queryClient.invalidateQueries({ queryKey: [INIT_DATA_V2] });
    }, [queryClient]);
};

export const useMutateConfigValue = (options?: UseMutationOptions<void, unknown, TimeUserFeatures, unknown>) => {
    return useMutation({
        mutationFn: async (dto: TimeUserFeatures) => {
            return ApiCalls.configurationBasicCreate(dto).then(() => {});
        },
        ...(options || {}),
    });
};

export const useSetConfigValue = (options?: UseMutationOptions<void, unknown, TimeUserFeatures, unknown>) => {
    const { mutateAsync } = useMutateConfigValue(options);
    const setConfigValueCache = useSetConfigValuesCache();
    const invalidateV2InitData = useInvalidateV2InitData();
    return useCallback(
        (newConfig: FunctionOr<TimeUserFeatures>) => {
            return new Promise<void>((res, rej) => {
                setConfigValueCache(configs => {
                    const nextConfig = unwrap(newConfig, configs.configuration);
                    mutateAsync(nextConfig)
                        .then(() => {
                            invalidateV2InitData();
                            res();
                        })
                        .catch(rej);
                    return {
                        ...configs,
                        configuration: nextConfig,
                    };
                });
            });
        },
        [mutateAsync, setConfigValueCache, invalidateV2InitData],
    );
};

export const useConfigValuesState = (
    options?: Omit<UseQueryOptions<InitialData, unknown, InitialData, string[]>, 'initialData' | 'queryKey' | 'queryFn'> & {
        initialData?: () => undefined;
    },
) => {
    const { mutateAsync, isLoading } = useMutateConfigValue();
    const setConfigValueCache = useSetConfigValuesCache();
    const invalidateV2InitData = useInvalidateV2InitData();
    const setConfigValue = useCallback(
        (newConfig: FunctionOr<TimeUserFeatures>) => {
            return new Promise<void>((res, rej) => {
                setConfigValueCache(configs => {
                    const nextConfig = unwrap(newConfig, configs.configuration);
                    mutateAsync(nextConfig)
                        .then(() => {
                            invalidateV2InitData();
                            res();
                        })
                        .catch(rej);
                    return {
                        ...configs,
                        configuration: nextConfig,
                    };
                });
            });
        },
        [mutateAsync, setConfigValueCache, invalidateV2InitData],
    );
    const query = useConfigValues({ suspense: true, ...options });
    return {
        ...query,
        initialData: query.data,
        setConfigValue,
        setConfigValueCache,
        isLoading: query.isLoading || isLoading,
    };
};
